import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Alert from "@mui/material/Alert";
import { alpha, useTheme } from "@mui/material/styles";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useTranslation } from "../../hooks";
import { PortalCard } from "./ui/PortalCard";
import { PortalStatusPill, pillColor } from "./PortalStatusPill";
import { AppointmentActions } from "./AppointmentActions";
import { AppointmentDetails } from "./AppointmentDetails";
import { formatAppointmentDate, formatAppointmentDateParts, formatAppointmentTime, hasEnded, isAwaitingPaymentVerification, resolveStatusPill, } from "./appointmentPresentation";
import { isTerminalStatus } from "../../services/portal/types";
function MetaRow({ icon, children, }) {
    return (_jsxs(Stack, { direction: "row", spacing: 0.75, sx: { alignItems: "center" }, children: [_jsx(Box, { sx: {
                    display: "inline-flex",
                    color: "text.secondary",
                    flexShrink: 0,
                    "& svg": { fontSize: 15 },
                }, children: icon }), _jsx(Typography, { variant: "body2", sx: { color: "text.secondary", minWidth: 0, wordBreak: "break-word" }, children: children })] }));
}
function DateTile({ appointment, parts, accent, }) {
    if (!parts) {
        return (_jsx(Typography, { variant: "body2", sx: { fontWeight: 600, flexShrink: 0 }, children: formatAppointmentDate(appointment) }));
    }
    return (_jsxs(Box, { sx: {
            flexShrink: 0,
            width: { xs: 52, sm: 58 },
            borderRadius: 2,
            py: 0.875,
            textAlign: "center",
            lineHeight: 1,
            color: accent,
            backgroundColor: alpha(accent, 0.08),
            border: `1px solid ${alpha(accent, 0.16)}`,
        }, children: [_jsx(Box, { sx: {
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    opacity: 0.85,
                }, children: parts.weekday }), _jsx(Box, { sx: { fontSize: { xs: 21, sm: 23 }, fontWeight: 700, my: 0.375 }, children: parts.day }), _jsx(Box, { sx: {
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    opacity: 0.85,
                }, children: parts.month })] }));
}
export function AppointmentCard({ appointment, tab, showPatientName, hasManualPaymentMethods, busyAppointmentId, busyAttestStatus, attestError, onCancel, onReschedule, onSelfAttest, onClaimPayment, }) {
    const { t } = useTranslation("portal");
    const theme = useTheme();
    const [expanded, setExpanded] = React.useState(false);
    const pill = resolveStatusPill(appointment);
    const busy = busyAppointmentId === appointment.id;
    const time = formatAppointmentTime(appointment);
    const isOnline = (appointment.mode ?? "").toUpperCase().includes("ONLINE");
    const joinUrl = isOnline &&
        !hasEnded(appointment) &&
        !isTerminalStatus(appointment.status)
        ? appointment.videoMeetingUrl
        : null;
    const awaitingVerification = isAwaitingPaymentVerification(appointment);
    const parts = formatAppointmentDateParts(appointment);
    const accent = pill.tone === "neutral"
        ? theme.palette.primary.main
        : pillColor(pill.tone, theme);
    const place = [
        isOnline ? t("modeOnline", "Online") : t("modeInClinic", "In-clinic"),
        appointment.locationAddress,
    ]
        .filter(Boolean)
        .join(" · ");
    return (_jsxs(PortalCard, { accentColor: alpha(accent, pill.tone === "neutral" ? 0.3 : 1), children: [_jsxs(Box, { sx: { p: { xs: 1.75, sm: 2.25 } }, children: [_jsxs(Stack, { direction: "row", spacing: { xs: 1.5, sm: 2 }, children: [_jsx(DateTile, { appointment: appointment, parts: parts, accent: accent }), _jsxs(Box, { sx: { minWidth: 0, flex: 1 }, children: [_jsxs(Stack, { direction: "row", spacing: 1, sx: { alignItems: "flex-start", justifyContent: "space-between" }, children: [_jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 700, lineHeight: 1.3 }, children: appointment.doctorName || "—" }), appointment.doctorSpecialization ? (_jsx(Typography, { variant: "caption", sx: { color: "text.secondary", display: "block" }, children: appointment.doctorSpecialization })) : null] }), _jsx(Box, { sx: { flexShrink: 0 }, children: _jsx(PortalStatusPill, { pill: pill }) })] }), _jsxs(Stack, { spacing: 0.5, sx: { mt: 1.25 }, children: [time || parts?.relativeKey ? (_jsxs(MetaRow, { icon: _jsx(AccessTimeRoundedIcon, {}), children: [_jsx(Box, { component: "span", sx: { color: "text.primary", fontWeight: 600 }, children: time ?? "" }), parts?.relativeKey ? (_jsx(Box, { component: "span", sx: {
                                                            display: "inline-block",
                                                            ml: time ? 0.75 : 0,
                                                            px: 0.75,
                                                            py: 0.125,
                                                            borderRadius: 999,
                                                            fontSize: 11,
                                                            fontWeight: 700,
                                                            color: accent,
                                                            backgroundColor: alpha(accent, 0.1),
                                                            whiteSpace: "nowrap",
                                                        }, children: parts.relativeKey === "today"
                                                            ? t("today", "Today")
                                                            : t("tomorrow", "Tomorrow") })) : null] })) : null, place ? (_jsx(MetaRow, { icon: isOnline ? _jsx(VideocamOutlinedIcon, {}) : _jsx(PlaceOutlinedIcon, {}), children: place })) : null, showPatientName && appointment.patientName ? (_jsx(MetaRow, { icon: _jsx(PersonOutlineRoundedIcon, {}), children: t("forPatient", "For {{name}}", {
                                                    name: appointment.patientName,
                                                }) })) : null] }), joinUrl ? (_jsx(Button, { href: joinUrl, target: "_blank", rel: "noopener noreferrer", size: "small", variant: "contained", startIcon: _jsx(VideocamOutlinedIcon, {}), sx: { mt: 1.5, py: 0.625, px: 2 }, children: t("joinMeeting", "Join video call") })) : null] })] }), awaitingVerification ? (_jsx(Alert, { severity: "warning", variant: "outlined", sx: { mt: 1.75, py: 0.25, alignItems: "center" }, children: t("claimPending", "Payment pending verification — keep your reference handy.") })) : null, _jsx(AppointmentActions, { appointment: appointment, tab: tab, hasManualPaymentMethods: hasManualPaymentMethods, busy: busy, busyAttestStatus: busy ? busyAttestStatus : null, attestError: attestError, onCancel: onCancel, onReschedule: onReschedule, onSelfAttest: onSelfAttest, onClaimPayment: onClaimPayment })] }), _jsxs(Box, { sx: (muiTheme) => ({
                    borderTop: `1px solid ${muiTheme.palette.divider}`,
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.02),
                }), children: [_jsx(Button, { fullWidth: true, size: "small", onClick: () => setExpanded((open) => !open), "aria-expanded": expanded, endIcon: _jsx(ExpandMoreRoundedIcon, { sx: {
                                transition: "transform 160ms ease",
                                transform: expanded ? "rotate(180deg)" : "none",
                                "@media (prefers-reduced-motion: reduce)": {
                                    transition: "none",
                                },
                            } }), sx: {
                            color: "text.secondary",
                            fontWeight: 600,
                            fontSize: 12,
                            py: 0.875,
                            borderRadius: 0,
                        }, children: expanded
                            ? t("hideDetails", "Hide details")
                            : t("viewDetails", "View details") }), _jsx(Collapse, { in: expanded, unmountOnExit: true, children: _jsx(Box, { sx: { px: { xs: 1.75, sm: 2.25 }, pb: 2 }, children: _jsx(AppointmentDetails, { appointment: appointment }) }) })] })] }));
}
